const PORTAL_API_URL =
  process.env.NEXT_PUBLIC_PORTAL_API_URL || 'https://api.sekanipay.com/api/v1/portal';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PortalUser {
  id: number;
  name: string;
  email: string;
  roles?: string[];
  permissions?: string[];
}

export interface PortalAuthResponse {
  token: string;
  user: PortalUser;
}

export interface PortalPendingResponse {
  requires_verification: true;
  email: string;
  message: string;
}

export type PortalRegisterResponse = PortalAuthResponse | PortalPendingResponse;

export function isPendingVerification(
  res: PortalRegisterResponse | PortalAuthResponse | PortalPendingResponse,
): res is PortalPendingResponse {
  return 'requires_verification' in res && res.requires_verification === true;
}

export interface PortalCompany {
  id: number;
  company_name: string;
  sub_domain: string;
  country: string | null;
  modules: string[] | null;
  env: Record<string, string> | null;
  created_at: string;
  updated_at: string;
}

export interface PortalApiError {
  message: string;
  errors?: Record<string, string[]>;
}

// ── Fetch helper ──────────────────────────────────────────────────────────────

async function portalFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${PORTAL_API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...options.headers,
    },
  });

  if (!res.ok) {
    const error: PortalApiError = await res.json().catch(() => ({ message: 'Request failed' }));
    throw error;
  }

  return res.json();
}

/** Bearer authorization header for authenticated portal requests. */
function bearer(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` };
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function portalRegister(data: {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}): Promise<PortalRegisterResponse> {
  return portalFetch('/register', { method: 'POST', body: JSON.stringify(data) });
}

export async function portalLogin(data: {
  email: string;
  password: string;
}): Promise<PortalAuthResponse> {
  // Login may return 403 with requires_verification — handled by caller
  const res = await fetch(`${PORTAL_API_URL}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(data),
  });

  const body = await res.json().catch(() => ({ message: 'Request failed' }));

  // 403 with requires_verification is not an error — return it
  if (res.status === 403 && body.requires_verification) {
    return body as PortalPendingResponse as unknown as PortalAuthResponse;
  }

  if (!res.ok) {
    throw body as PortalApiError;
  }

  return body;
}

export async function portalVerifyEmail(data: {
  email: string;
  code: string;
}): Promise<PortalAuthResponse> {
  return portalFetch('/verify-email', { method: 'POST', body: JSON.stringify(data) });
}

export async function portalResendVerification(data: {
  email: string;
}): Promise<{ message: string }> {
  return portalFetch('/resend-verification', { method: 'POST', body: JSON.stringify(data) });
}

// ── Companies ─────────────────────────────────────────────────────────────────

export async function portalGetCompanies(
  token: string,
): Promise<{ data: PortalCompany[] }> {
  return portalFetch('/companies', {
    headers: bearer(token),
  });
}

export async function portalCreateCompany(
  token: string,
  data: {
    company_name: string;
    sub_domain: string;
    country?: string;
    modules?: string[];
  },
): Promise<{ data: PortalCompany }> {
  return portalFetch('/companies', {
    method: 'POST',
    body: JSON.stringify(data),
    headers: bearer(token),
  });
}

export async function portalProvisionCompany(
  token: string,
  companyId: number,
): Promise<{ message: string; database: string }> {
  return portalFetch(`/companies/${companyId}/provision`, {
    method: 'POST',
    headers: bearer(token),
  });
}

export async function portalCheckSubdomain(
  subdomain: string,
): Promise<{ available: boolean; sub_domain: string }> {
  return portalFetch('/check-subdomain', {
    method: 'POST',
    body: JSON.stringify({ sub_domain: subdomain }),
  });
}
